import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { authService } from './authService'; // Assuming authService will be in the same directory
import { getProjectId } from '../utils/auth';
import { cachedRequest } from './requestCache';

// Ensure this environment variable is set in your dashboard project's .env file
const API_URL = process.env.REACT_APP_ANALYTICS_SERVICE_URL || 'https://api.floorselector.convrse.ai'; // Fallback URL
// const API_URL = 'http://localhost:8000';
// Exportable request interceptor logic
export const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = authService.getToken();
    const tokenType = localStorage.getItem('token_type') || 'Bearer';

    if (token) {
        config.headers.Authorization = `${tokenType} ${token}`;
    }

    const projectId = getProjectId();
    if (projectId && config.method?.toLowerCase() === 'get') {
        config.params = {
            ...config.params,
            project_id: projectId
        };
    }
    return config;
};

export const requestErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
};

// Exportable response interceptor logic
export const responseInterceptor = (response: AxiosResponse): AxiosResponse => response;

export const responseErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
    if (error.response?.status === 401) {
        console.warn('Unauthorized request - logging out.');
        authService.logout();
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }
    return Promise.reject(error);
};

// Create API instance
export const api = axios.create({
    baseURL: API_URL
});

/**
 * Humanizes API error messages, converting technical errors to user-friendly messages
 */
const humanizeErrorMessage = (error: any): string => {
    // Check for network errors (no response from server)
    if (!error.response) {
        // Network request failed
        if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
            return 'Unable to connect to the server. Please check your internet connection and try again.';
        }

        // Connection timeout
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            return 'The request took too long. Please check your internet connection and try again.';
        }

        // DNS/Connection errors
        if (error.code === 'EHOSTUNREACH' || error.message?.includes('EHOSTUNREACH')) {
            return 'Unable to reach the server. Please check your internet connection and try again.';
        }

        if (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) {
            return 'Connection refused. The server may be temporarily unavailable. Please try again later.';
        }

        if (error.code === 'ENOTFOUND' || error.message?.includes('ENOTFOUND')) {
            return 'Server not found. Please check your internet connection and try again.';
        }

        // Generic network error
        return 'Network error occurred. Please check your internet connection and try again.';
    }

    // Extract error message from API response
    let message = error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'An unknown error occurred';

    // Check the details field for network errors (common in API responses)
    const details = error.response?.data?.details || error.response?.data?.detail || '';
    const detailsString = String(details).toLowerCase();

    // Convert string to check for technical error patterns
    const errorString = String(message).toLowerCase();

    // Check for database connection errors in the details field first (most common case)
    if (detailsString.includes('ehostunreach') || detailsString.includes('connect ehostunreach')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    // Check for database connection errors in the message field
    if (errorString.includes('ehostunreach') || errorString.includes('connect ehostunreach')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    // Check details field for other network errors
    if (detailsString.includes('econnrefused') || detailsString.includes('connection refused')) {
        return 'Connection refused. The server may be temporarily unavailable. Please try again later.';
    }

    if (detailsString.includes('timeout') || detailsString.includes('timed out')) {
        return 'The request took too long. Please try again.';
    }

    if (errorString.includes('econnrefused') || errorString.includes('connection refused')) {
        return 'Connection refused. The server may be temporarily unavailable. Please try again later.';
    }

    if (errorString.includes('timeout') || errorString.includes('timed out')) {
        return 'The request took too long. Please try again.';
    }

    if (errorString.includes('network') && errorString.includes('error')) {
        return 'Network error occurred. Please check your internet connection and try again.';
    }

    // Check for specific HTTP status codes
    if (error.response?.status === 404) {
        return message || 'The requested resource was not found.';
    }

    if (error.response?.status === 500) {
        return 'Server error occurred. Please try again later or contact support if the problem persists.';
    }

    if (error.response?.status === 503) {
        return 'Service temporarily unavailable. Please try again later.';
    }

    if (error.response?.status === 504) {
        return 'Request timeout. The server took too long to respond. Please try again.';
    }

    // Return the message as-is if it's already user-friendly
    return message;
};

// Function to handle API errors (moved here for potential reuse)
export const handleApiError = (error: any): never => {
    console.error("API Error:", error);
    const humanizedMessage = humanizeErrorMessage(error);
    throw new Error(humanizedMessage);
};

// Add auth token and project ID to API requests
api.interceptors.request.use(requestInterceptor, requestErrorInterceptor);

// Handle 401 responses
api.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Transparently cache + de-duplicate read-only analytics GET requests. The
// Dashboard (home) page and its ~25 chart components each fire their own GET
// on mount, so without caching every revisit re-fetches the whole page and
// re-shows spinners. We only cache endpoints that return aggregated, slowly-
// changing analytics data — transactional reads (meetings, clients, etc.) are
// left untouched so they always reflect the latest writes.
const CACHEABLE_GET_PATTERNS = [
  '/aggregations/',
  '/sessions/',
  '/api/dashboard/',
  '/interactions/',
  '/api/clients', // clients list + client-by-id (profile pages)
  '/sales-person/', // sales-team list + per-person overview
  '/salespersons/', // sales-person details (profile pages)
];

const isCacheableGet = (url: string): boolean =>
  CACHEABLE_GET_PATTERNS.some((pattern) => url.includes(pattern));

const rawGet = api.get.bind(api);
api.get = function cachedApiGet(this: unknown, url: string, config?: any) {
  // Allow explicit opt-out via { cache: false }, and only cache known
  // read-only analytics endpoints.
  if ((config && config.cache === false) || !isCacheableGet(url)) {
    return rawGet(url, config);
  }
  const projectId = getProjectId() || '';
  const paramsKey = JSON.stringify(config?.params ?? {});
  const key = `GET ${url} ${paramsKey} pid=${projectId}`;
  return cachedRequest(key, () => rawGet(url, config));
} as typeof api.get;

// Re-exporting types defined in ../types/analytics.ts for convenience
export type { ApiResponse, PaginatedResponse } from '../types/analytics'; 